import React from 'react';
import LegalPageLayout, { Section, Paragraph, List, Highlight } from '../../components/LegalPageLayout';

const AcceptableUsePolicy = () => {
    return (
        <LegalPageLayout title="Acceptable Use Policy" lastUpdated="March 2026">
            <Section title="Purpose">
                <Paragraph>
                    This Acceptable Use Policy outlines the standards of conduct expected from all 
                    ClientCheck users. Our platform is designed to help contractors share professional 
                    job experiences—not to enable harassment, defamation, or abuse.
                </Paragraph>
                <Paragraph>
                    By using ClientCheck, you agree to abide by this policy. Violations may result 
                    in content removal, account suspension, or permanent termination.
                </Paragraph>
            </Section>

            <Section title="Prohibited Content">
                <Paragraph>
                    The following types of content are strictly prohibited on ClientCheck:
                </Paragraph>
                
                <h3 className="font-medium text-foreground mt-4 mb-2">Harmful Language</h3>
                <List items={[
                    "Harassment, threats, or intimidation",
                    "Profanity, vulgar language, or offensive content",
                    "Hate speech or discriminatory language",
                    "Personal insults or attacks"
                ]} />
                
                <h3 className="font-medium text-foreground mt-4 mb-2">False or Misleading Content</h3>
                <List items={[
                    "Knowingly false information",
                    "Defamatory statements without factual basis",
                    "Criminal accusations or allegations of illegal activity",
                    "Misleading content intended to deceive other users"
                ]} />
                
                <h3 className="font-medium text-foreground mt-4 mb-2">Private Information</h3>
                <List items={[
                    "Personal names of individuals at job addresses",
                    "Phone numbers, email addresses, or other contact details",
                    "Home addresses of individuals (as opposed to job site addresses)",
                    "Financial information or account details"
                ]} />
            </Section>

            <Section title="Prohibited Activities">
                <Paragraph>
                    The following activities are prohibited on ClientCheck:
                </Paragraph>
                <List items={[
                    "Impersonating another person or contractor",
                    "Submitting reviews for jobs you did not perform",
                    "Creating multiple accounts to manipulate reviews or scores",
                    "Attempting to manipulate the experience summary system",
                    "Attempting to bypass content moderation filters",
                    "Using the platform for competitive sabotage",
                    "Soliciting or exchanging reviews for compensation",
                    "Automated scraping or data extraction without authorization"
                ]} />
            </Section>

            <Section title="Professional Standards">
                <Paragraph>
                    All users are expected to maintain professional standards:
                </Paragraph>
                <List items={[
                    "Write reviews that are factual and job-related",
                    "Use professional language and neutral tone",
                    "Focus on job experiences, not personal grievances",
                    "Respect other users and the contractor community",
                    "Report policy violations through proper channels"
                ]} />
            </Section>

            <Section title="Enforcement">
                <Paragraph>
                    ClientCheck enforces this policy through multiple mechanisms:
                </Paragraph>
                <List items={[
                    "Automated content filtering that blocks prohibited language",
                    "Human review of flagged content by our moderation team",
                    "User reporting of policy violations",
                    "Algorithmic detection of manipulation attempts"
                ]} />
                
                <Highlight>
                    Violations may result in: content removal, written warnings, temporary 
                    suspension, permanent account termination, or legal action in severe cases.
                </Highlight>
            </Section>

            <Section title="Reporting Violations">
                <Paragraph>
                    If you encounter content that violates this policy, you can report it using 
                    the flag feature on reviews or by contacting our support team. All reports 
                    are reviewed by our moderation team.
                </Paragraph>
                <Paragraph>
                    We take reports seriously and investigate all credible claims of policy violations.
                </Paragraph>
            </Section>

            <Section title="Appeals">
                <Paragraph>
                    If you believe your content was removed in error or your account was wrongly 
                    suspended, you may submit an appeal to our support team. Appeals are reviewed 
                    on a case-by-case basis, and decisions may be final.
                </Paragraph>
            </Section>
        </LegalPageLayout>
    );
};

export default AcceptableUsePolicy;
